import { useSelector } from "react-redux";
import IAssignUserScopeGrantsRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/assign-user-scope-grants-request-dto";
import IChangeOrganizationUserRoleRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/change-organization-user-role-request-dto";
import ICreateOrganizationUserRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/create-organization-user-request-dto";
import IInviteUserRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/invite-user-request-dto";
import ISetOrganizationUserActivationRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/set-organization-user-activation-request-dto";
import IViewOrganizationUsersRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/view-organization-users-request-dto";
import IAssignUserScopeGrantsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/assign-user-scope-grants-response-dto";
import IChangeOrganizationUserRoleResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/change-organization-user-role-response-dto";
import ICreateOrganizationUserResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/create-organization-user-response-dto";
import IInviteUserResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/invite-user-response-dto";
import ISetOrganizationUserActivationResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/set-organization-user-activation-response-dto";
import IViewCurrentUserAccessResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-current-user-access-response-dto";
import IViewOrganizationUsersResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-organization-users-response-dto";
import IViewScopeTreeResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-scope-tree-response-dto";
import IViewSeatCapsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-seat-caps-response-dto";
import IViewUserScopeGrantsResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/view-user-scope-grants-response-dto";
import { transformErrorResponse } from "../../../shared/oversight-core/utils/transformErrorResponse";
import { selectAuth } from "../../features/auth/auth-slice";
import { rootApi } from "../apiManager";
import { toQueryString } from "../query-string";

interface ITenantUserArgs {
  tenantId: string;
  userId: string;
}

export const orgUserAPI = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // The token is only the cache key, so a new sign-in never sees the previous user's access.
    getCurrentAccess: builder.query<IViewCurrentUserAccessResponseDTO, string>({
      transformErrorResponse,
      query() {
        return { url: "admin/current/access", method: "GET" };
      },
    }),
    getOrganizationUsers: builder.query<
      IViewOrganizationUsersResponseDTO,
      { tenantId: string } & IViewOrganizationUsersRequestDTO
    >({
      transformErrorResponse,
      query({ tenantId, ...params }) {
        return {
          url: `admin/tenants/${tenantId}/users${toQueryString({ ...params })}`,
          method: "GET",
        };
      },
      providesTags: ["OrgUsers"],
    }),
    getSeatCaps: builder.query<IViewSeatCapsResponseDTO, string>({
      transformErrorResponse,
      query(tenantId) {
        return { url: `admin/tenants/${tenantId}/seat-caps`, method: "GET" };
      },
      providesTags: ["SeatCaps"],
    }),
    getScopeNodes: builder.query<IViewScopeTreeResponseDTO, string>({
      transformErrorResponse,
      query(tenantId) {
        return { url: `admin/tenants/${tenantId}/scope-nodes`, method: "GET" };
      },
    }),
    getUserScopeGrants: builder.query<
      IViewUserScopeGrantsResponseDTO,
      ITenantUserArgs
    >({
      transformErrorResponse,
      query({ tenantId, userId }) {
        return {
          url: `admin/tenants/${tenantId}/users/${userId}/scope-grants`,
          method: "GET",
        };
      },
    }),
    assignUserScopeGrants: builder.mutation<
      IAssignUserScopeGrantsResponseDTO,
      ITenantUserArgs & IAssignUserScopeGrantsRequestDTO
    >({
      transformErrorResponse,
      query({ tenantId, userId, ...body }) {
        return {
          url: `admin/tenants/${tenantId}/users/${userId}/scope-grants`,
          method: "PUT",
          body,
        };
      },
      // Grants decide the seat type: only View redacted takes a guest seat.
      invalidatesTags: ["OrgUsers", "SeatCaps"],
    }),
    changeOrganizationUserRole: builder.mutation<
      IChangeOrganizationUserRoleResponseDTO,
      ITenantUserArgs & IChangeOrganizationUserRoleRequestDTO
    >({
      transformErrorResponse,
      query({ tenantId, userId, ...body }) {
        return {
          url: `admin/tenants/${tenantId}/users/${userId}/role`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["OrgUsers"],
    }),
    setOrganizationUserActivation: builder.mutation<
      ISetOrganizationUserActivationResponseDTO,
      ITenantUserArgs & ISetOrganizationUserActivationRequestDTO
    >({
      transformErrorResponse,
      query({ tenantId, userId, ...body }) {
        return {
          url: `admin/tenants/${tenantId}/users/${userId}/activation`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: ["OrgUsers", "SeatCaps"],
    }),
    createOrganizationUser: builder.mutation<
      ICreateOrganizationUserResponseDTO,
      { tenantId: string } & ICreateOrganizationUserRequestDTO
    >({
      transformErrorResponse,
      query({ tenantId, ...body }) {
        return {
          url: `admin/tenants/${tenantId}/users`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["OrgUsers", "SeatCaps"],
    }),
    resendInvitation: builder.mutation<
      IInviteUserResponseDTO,
      { userId: string } & IInviteUserRequestDTO
    >({
      transformErrorResponse,
      query({ userId, ...body }) {
        return {
          url: `user/${userId}/invite`,
          method: "POST",
          body,
        };
      },
    }),
  }),
});

export const {
  useGetCurrentAccessQuery,
  useGetOrganizationUsersQuery,
  useGetSeatCapsQuery,
  useGetScopeNodesQuery,
  useLazyGetUserScopeGrantsQuery,
  useAssignUserScopeGrantsMutation,
  useChangeOrganizationUserRoleMutation,
  useSetOrganizationUserActivationMutation,
  useCreateOrganizationUserMutation,
  useResendInvitationMutation,
} = orgUserAPI;

// GET /api/admin/current/access, fetched once per sign-in and shared by every caller.
export const useCurrentAccess = () => {
  const { token } = useSelector(selectAuth);
  return useGetCurrentAccessQuery(token ?? "", { skip: !token });
};
